import { useMemo } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Typography,
  Spin,
  Alert,
} from 'antd';
import {
  AppstoreOutlined,
  InboxOutlined,
  DollarOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useWarehouseDashboard } from '@hooks/useAnalytics';
import type { LowStockItem, CategoryStock } from '@/types/analytics';

export default function WarehouseDashboardPage() {
  const { data: mdData, isLoading: mdIsLoading } = useWarehouseDashboard();

  const mdMaxCategoryValue = useMemo(() => {
    if (!mdData?.stockByCategory || mdData.stockByCategory.length === 0) return 1;
    return Math.max(...mdData.stockByCategory.map((mdItem) => mdItem.totalValue), 1);
  }, [mdData]);

  const mdLowStockColumns: ColumnsType<LowStockItem> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'На складі',
      dataIndex: 'quantityOnHand',
      key: 'quantityOnHand',
      width: 100,
      align: 'right',
      render: (mdValue: number) => (
        <Typography.Text type={mdValue <= 0 ? 'danger' : 'warning'}>
          {mdValue}
        </Typography.Text>
      ),
    },
    {
      title: 'Зарезервовано',
      dataIndex: 'quantityReserved',
      key: 'quantityReserved',
      width: 120,
      align: 'right',
    },
  ];

  const mdCategoryColumns: ColumnsType<CategoryStock> = [
    {
      title: 'Категорія',
      dataIndex: 'category',
      key: 'category',
      ellipsis: true,
    },
    {
      title: 'Товарів',
      dataIndex: 'productCount',
      key: 'productCount',
      width: 90,
      align: 'right',
    },
    {
      title: 'Партій',
      dataIndex: 'batchCount',
      key: 'batchCount',
      width: 90,
      align: 'right',
    },
    {
      title: 'Вартість, UAH',
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 200,
      render: (mdValue: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: `${(mdValue / mdMaxCategoryValue) * 100}%`,
              height: 16,
              backgroundColor: '#1677ff',
              borderRadius: 2,
              minWidth: 2,
            }}
          />
          <Typography.Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 0 })}
          </Typography.Text>
        </div>
      ),
    },
  ];

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdData) {
    return <Alert type="warning" message="Дані складу недоступні" showIcon />;
  }

  const mdExpiryItems = [
    { label: '30 днів', value: mdData.expiringIn30Days, color: '#ff4d4f' },
    { label: '60 днів', value: mdData.expiringIn60Days, color: '#fa8c16' },
    { label: '90 днів', value: mdData.expiringIn90Days, color: '#faad14' },
    { label: 'Прострочені', value: mdData.expiredBatches, color: '#8b0000' },
    { label: 'Заблоковані', value: mdData.blockedBatches, color: '#595959' },
    { label: 'Відкликані', value: mdData.recalledBatches, color: '#cf1322' },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Дашборд складу
      </Typography.Title>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Всього SKU"
              value={mdData.totalSkuCount}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Всього партій"
              value={mdData.totalBatchCount}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Вартість складу"
              value={mdData.totalStockValue}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Прострочених"
              value={mdData.expiredBatches}
              prefix={<WarningOutlined />}
              valueStyle={{ color: mdData.expiredBatches > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Expiry breakdown */}
      <Card title="Терміни придатності партій" size="small" style={{ marginBottom: 24 }}>
        <Row gutter={[12, 12]}>
          {mdExpiryItems.map((mdItem) => (
            <Col span={4} key={mdItem.label}>
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 8,
                  backgroundColor: mdItem.value > 0 ? `${mdItem.color}10` : '#fafafa',
                  border: `1px solid ${mdItem.value > 0 ? mdItem.color : '#f0f0f0'}`,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    color: mdItem.value > 0 ? mdItem.color : '#8c8c8c',
                  }}
                >
                  {mdItem.value}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                  {mdItem.label}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Two tables side by side */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Запаси за категоріями" size="small">
            <Table<CategoryStock>
              columns={mdCategoryColumns}
              dataSource={mdData.stockByCategory}
              rowKey="category"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Немає даних' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Низькі залишки" size="small">
            <Table<LowStockItem>
              columns={mdLowStockColumns}
              dataSource={mdData.lowStockItems}
              rowKey="productId"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Немає критичних залишків' }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
